import React, { FC, useMemo, useState } from 'react';
import { Box, CircularProgress, Container, Paper, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { useGetResourceQuery } from '../healthWalletSlice';
import { formatDate, recordStats } from '../healthWalletConstants';
import type { GroupedSummary } from '../healthWalletConstants';
import type { SectionProps } from './TabRenderer';
import { bucketMedGroup, medStatusChip } from './classify';
import { V1_ACCENT, V1_ACCENT_TINT, V1_ACCENT_BORDER } from './theme';
import { V1SearchField } from './V1SearchField';

// ============================================================================
// Medications tab sections (design: Polygon Health Trends Redesign/Medications
// Desktop.dc.html, in the DESIGN palette — see theme.ts). Data = tier-1 groups
// from get_patient_resource('medications') incl. dose/route/indication fields.
// Recorded decisions (project_hw_medications_tab_plan): NAME grouping (RxNorm
// rollup deferred); Infusions = route-detected (no MedicationAdministration
// patient-facing — section auto-hides when empty); the design's admin-route
// junk section SKIPPED (no data source); indication sub-headers from
// reasonCode with the design's "Indication not documented" fallback.
// ============================================================================

const PAGE_WIDTH_SX = { maxWidth: { xs: '100%', sm: '94%' } };
const CARD_LIMIT = 6;

// SINGLE source of truth for the tab's buckets — the hero tiles, filter pills, and expandable sections all
// iterate THIS list (present-only: each renders only when its bucket is non-empty), so the three can't drift
// out of sync (previously the hero was a hand-picked 3 that silently dropped "Other records"). `label` = pill
// text + hero tile label; `section` = section header; `tileBg/tileFg` = hero tile palette; `dot` = section dot.
const MED_BUCKETS = [
  { key: 'current',  label: 'Current',       section: 'Current medications',     dot: '#3b7dd8', tileBg: '#eef3fb',     tileFg: '#2f5fb0' },
  { key: 'past',     label: 'Past',          section: 'Past medications',        dot: '#b0b0b6', tileBg: '#f0f0f3',     tileFg: '#48484a' },
  { key: 'infusion', label: 'Infusions',     section: 'Infusions & IV therapy',  dot: V1_ACCENT, tileBg: V1_ACCENT_TINT, tileFg: V1_ACCENT },
  { key: 'record',   label: 'Other records', section: 'Other treatment records', dot: '#c7c7cc', tileBg: '#f4f4f6',     tileFg: '#8e8e93' },
] as const;

const useMedicationGroups = () => {
  const { data, isLoading } = useGetResourceQuery('medications');
  const groups: GroupedSummary[] = useMemo(
    () => (data ? [...data.named, ...(data.other ? [{ ...data.other, name: data.other.name || 'Other / Uncategorized' }] : [])] : []),
    [data]
  );
  return { groups, totalRecords: data?.totalRecords ?? 0, rawRecords: data?.rawRecords ?? 0, isLoading };
};

// --- 1. Hero "Your record at a glance" -------------------------------------
export const MedicationsHero: FC<SectionProps> = () => {
  const { groups, totalRecords, rawRecords, isLoading } = useMedicationGroups();
  const { x, y, grouped } = recordStats(totalRecords, groups.length, rawRecords);
  const counts = groups.reduce((acc, g) => { const k = bucketMedGroup(g); acc[k] = (acc[k] ?? 0) + 1; return acc; }, {} as Record<string, number>);
  if (isLoading) {
    return <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2, display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: V1_ACCENT }} /></Container>;
  }
  const tile = (value: number | string, label: string, bg: string, fg: string) => (
    <Box sx={{ width: { xs: 92, sm: 104 }, flexShrink: 0, bgcolor: bg, borderRadius: '15px', p: '16px 10px', textAlign: 'center' }}>
      <Typography sx={{ fontSize: { xs: 20, sm: 28 }, fontWeight: 700, color: fg, lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ fontSize: 11, color: fg, mt: 0.75, fontWeight: 600 }}>{label}</Typography>
    </Box>
  );
  return (
    <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2 }}>
      <Paper elevation={0} sx={{ borderRadius: '20px', p: '24px 26px', border: `1px solid ${V1_ACCENT_BORDER}`, bgcolor: 'white', display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 300 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, color: V1_ACCENT, textTransform: 'uppercase', mb: 1.25 }}>
            Your record at a glance
          </Typography>
          <Typography sx={{ fontSize: 21, fontWeight: 600, lineHeight: 1.4, letterSpacing: '-0.2px', maxWidth: 580 }}>
            {grouped ? (
              // X > Y: records repeat over time → tell the volume + grouping story.
              <>Your record contains <strong>{x.toLocaleString('en-US')}</strong> medication entries. We&rsquo;ve grouped them into <strong>{y.toLocaleString('en-US')}</strong> medications.</>
            ) : (
              // X = Y: each medication appears once → just the total (the sum of the tiles), no collapse claim.
              <>Your record contains <strong>{y.toLocaleString('en-US')}</strong> {y === 1 ? 'medication' : 'medications'}.</>
            )}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {MED_BUCKETS.filter((b) => (counts[b.key] ?? 0) > 0).map((b) => (
            <React.Fragment key={b.key}>{tile(counts[b.key] ?? 0, b.label, b.tileBg, b.tileFg)}</React.Fragment>
          ))}
        </Box>
      </Paper>
    </Container>
  );
};

// --- 2. Explainer — static "you may see many records" copy (reworded from "why are there so many
// records?" so it never has to assert a threshold count). Hidden only when the tab has NO records
// (the richer no-records empty-state — reasons / failed orgs — is a separate deferred item:
// project_hw_empty_tab_and_no_records_plan).
export const MedicationsExplainer: FC<SectionProps> = () => {
  const { totalRecords } = useMedicationGroups();
  if (totalRecords === 0) return null;
  return (
    <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2 }}>
      <Box sx={{ bgcolor: V1_ACCENT_TINT, borderRadius: '16px', p: '16px 20px', display: 'flex', gap: 1.75, alignItems: 'flex-start' }}>
        <Box sx={{ flex: 'none', width: 26, height: 26, borderRadius: '50%', bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <InfoOutlinedIcon sx={{ color: V1_ACCENT, fontSize: 18 }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 650, mb: 0.4 }}>You may see many records</Typography>
          <Typography sx={{ fontSize: 13, color: '#4a4a52', lineHeight: 1.5, maxWidth: 820 }}>
            Each prescription, refill, and pharmacy dispense is documented separately, so one medication you take regularly can
            appear as dozens of records over time. A high record count usually reflects ongoing use — not dozens of different
            treatments. Sections start collapsed so you can open just what you want to see.
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

// --- 3+4+5. Filters + expandable sections + cards ---------------------------
type FilterKey = 'all' | 'current' | 'past' | 'infusion' | 'record';

const CARD_DATE = (d: string) => (d ? formatDate(d) : '—');

const MedicationCard: FC<{ g: GroupedSummary; onOpen: () => void }> = ({ g, onOpen }) => {
  const chip = medStatusChip(g);
  const subtitle = [g.dose, g.route].filter(Boolean).join(' · ');
  return (
    <Box onClick={onOpen} sx={{
      bgcolor: '#fdfcff', border: '1px solid #ece9f4', borderRadius: '14px', p: 2.25, cursor: 'pointer',
      '&:hover': { borderColor: V1_ACCENT_BORDER, bgcolor: 'white' },
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.25 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 650, letterSpacing: '-0.2px', lineHeight: 1.25 }}>
            {g.name}
            {/* Alternate source names merged into this card (brands/spelling variants), muted in parens. */}
            {g.aka?.length ? <Box component="span" sx={{ fontWeight: 500, color: '#8e8e93' }}> ({g.aka.join(', ')})</Box> : null}
          </Typography>
          {/* One-line clamp: the dose falls back to a free-text sig when nothing structured exists. */}
          {subtitle ? <Typography sx={{ fontSize: 12, color: '#6b6b70', mt: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 260 }}>{subtitle}</Typography> : null}
        </Box>
        {chip ? (
          <Box sx={{ flex: 'none', fontSize: '10.5px', fontWeight: 700, px: 1.25, py: 0.5, borderRadius: '20px', bgcolor: chip.bg, color: chip.fg, whiteSpace: 'nowrap' }}>
            {chip.label}
          </Box>
        ) : null}
      </Box>
      <Box sx={{ display: 'flex', gap: 2.5, mt: 1.75 }}>
        {/* Same calendar day for first & last → show one "Date recorded" (CARD_DATE is day-level). */}
        {g.firstDate && CARD_DATE(g.firstDate) === CARD_DATE(g.lastDate) ? (
          <Box><Typography sx={{ fontSize: 11, color: '#aeaeb2', fontWeight: 600, whiteSpace: 'nowrap' }}>Date recorded</Typography><Typography sx={{ fontSize: 13, fontWeight: 600, mt: 0.25 }}>{CARD_DATE(g.firstDate)}</Typography></Box>
        ) : (
          <>
            <Box><Typography sx={{ fontSize: 11, color: '#aeaeb2', fontWeight: 600, whiteSpace: 'nowrap' }}>First documented</Typography><Typography sx={{ fontSize: 13, fontWeight: 600, mt: 0.25 }}>{CARD_DATE(g.firstDate)}</Typography></Box>
            <Box><Typography sx={{ fontSize: 11, color: '#aeaeb2', fontWeight: 600, whiteSpace: 'nowrap' }}>Most recent</Typography><Typography sx={{ fontSize: 13, fontWeight: 600, mt: 0.25 }}>{CARD_DATE(g.lastDate)}</Typography></Box>
          </>
        )}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.75, pt: 1.5, borderTop: '1px solid #f0eef6' }}>
        <Typography sx={{ fontSize: 12, color: '#6b6b70' }}>{g.count.toLocaleString('en-US')} records</Typography>
        <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: V1_ACCENT, whiteSpace: 'nowrap' }}>View history ›</Typography>
      </Box>
    </Box>
  );
};

export const MedicationsBody: FC<SectionProps> = ({ openDetail }) => {
  const { groups, isLoading } = useMedicationGroups();
  const [filter, setFilter] = useState<FilterKey>('all');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [showAll, setShowAll] = useState<Record<string, boolean>>({});

  const q = search.trim().toLowerCase();
  const matches = (g: GroupedSummary) => !q || (g.name || 'other').toLowerCase().includes(q) || (g.aka || []).some((a) => a.toLowerCase().includes(q));

  const buckets = useMemo(() => {
    const sorted = [...groups].sort((a, b) => b.count - a.count);
    return {
      current: sorted.filter((g) => bucketMedGroup(g) === 'current'),
      past: sorted.filter((g) => bucketMedGroup(g) === 'past'),
      infusion: sorted.filter((g) => bucketMedGroup(g) === 'infusion'),
      record: sorted.filter((g) => bucketMedGroup(g) === 'record'),
    };
  }, [groups]);

  if (isLoading) return null; // hero shows the spinner

  const pill = (label: string, active: boolean, onClick?: () => void, disabled = false) => (
    <Box key={label} onClick={disabled ? undefined : onClick} sx={{
      fontSize: 13, fontWeight: 600, px: 2, py: 1, borderRadius: '20px', cursor: disabled ? 'default' : 'pointer',
      bgcolor: active ? V1_ACCENT : 'white', color: disabled ? '#b9b9be' : active ? 'white' : '#3a3a3c',
      border: `1px solid ${active ? V1_ACCENT : '#e0e0e6'}`, opacity: disabled ? 0.6 : 1,
    }}>{label}</Box>
  );

  // Cards inside a section are sub-grouped by indication ("For Pain" — design), documented ones
  // first, then the "Indication not documented" remainder. Sub-headers only when any indication exists.
  const indicationGroups = (cards: GroupedSummary[]) => {
    const orderKeys: string[] = [];
    const byInd: Record<string, GroupedSummary[]> = {};
    cards.forEach((g) => {
      const k = g.indication || '__none';
      if (!byInd[k]) { byInd[k] = []; orderKeys.push(k); }
      byInd[k].push(g);
    });
    orderKeys.sort((a, b) => (a === '__none' ? 1 : 0) - (b === '__none' ? 1 : 0));
    const any = orderKeys.some((k) => k !== '__none');
    return { any, groups: orderKeys.map((k) => ({ key: k, label: k === '__none' ? 'Indication not documented' : `For ${k}`, cards: byInd[k] })) };
  };

  // Default-open = the FIRST NON-EMPTY section in display order (Leo 2026-07-14).
  const firstNonEmpty = MED_BUCKETS.map((b) => b.key).find((k) => buckets[k].length > 0);
  const defaultOpen = (key: string) => key === firstNonEmpty;

  const section = (key: FilterKey & string, title: string, dot: string, list: GroupedSummary[]) => {
    const visible = (filter === 'all' || filter === key) && list.filter(matches).length > 0;
    if (!visible) return null;
    const full = list.filter(matches);
    const forced = !!q || filter === key;
    const isOpen = forced ? true : (open[key] ?? defaultOpen(key));
    const all = !!q || !!showAll[key];
    const cards = isOpen ? (all ? full : full.slice(0, CARD_LIMIT)) : [];
    const ind = indicationGroups(cards);
    const grid = (cs: GroupedSummary[]) => (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))', gap: 1.75 }}>
        {cs.map((g, i) => <MedicationCard key={`${g.name}-${i}`} g={g} onOpen={() => openDetail(g)} />)}
      </Box>
    );
    return (
      <Paper key={key} elevation={0} sx={{ borderRadius: '18px', border: `1px solid ${V1_ACCENT_BORDER}`, bgcolor: 'white', overflow: 'hidden' }}>
        <Box onClick={() => setOpen((s) => ({ ...s, [key]: !(s[key] ?? defaultOpen(key)) }))} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '18px 22px', cursor: 'pointer' }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: dot, flex: 'none' }} />
          <Typography component="h2" sx={{ m: 0, fontSize: 18, fontWeight: 700, letterSpacing: '-0.3px', flex: 1 }}>{title}</Typography>
          <Box sx={{ fontSize: '12.5px', fontWeight: 700, color: '#6b6b70', bgcolor: '#f0f0f3', px: 1.4, py: 0.5, borderRadius: '20px' }}>{full.length}</Box>
          <Typography sx={{ fontSize: 13, color: '#8e8e93', width: 16, textAlign: 'center' }}>{isOpen ? '▾' : '▸'}</Typography>
        </Box>
        {isOpen && (
          <Box sx={{ px: 2.75, pb: 2.75 }}>
            {ind.any ? (
              ind.groups.map((grp) => (
                <Box key={grp.key} sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: '12.5px', fontWeight: 700, color: '#6b6b70', mb: 1, mt: 0.5 }}>
                    {grp.label} <Box component="span" sx={{ fontWeight: 600, color: '#aeaeb2' }}>· {grp.cards.length} {grp.cards.length === 1 ? 'medication' : 'medications'}</Box>
                  </Typography>
                  {grid(grp.cards)}
                </Box>
              ))
            ) : grid(cards)}
            {full.length > CARD_LIMIT && (
              <Typography onClick={() => setShowAll((s) => ({ ...s, [key]: !s[key] }))}
                sx={{ textAlign: 'center', mt: 2, fontSize: 13, fontWeight: 600, color: V1_ACCENT, cursor: 'pointer' }}>
                {all ? 'Show fewer' : `Show all ${full.length} →`}
              </Typography>
            )}
          </Box>
        )}
      </Paper>
    );
  };

  const noResults = !!q && !MED_BUCKETS.some((b) => buckets[b.key].some(matches));

  return (
    <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2.5, pb: 12 }}>
      <V1SearchField value={search} onChange={setSearch} placeholder="Search medications" />
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
        {pill('All', filter === 'all', () => setFilter('all'))}
        {/* Hide a classifier pill when its bucket is empty (nothing to show). */}
        {MED_BUCKETS.filter((b) => buckets[b.key].length > 0).map((b) => pill(b.label, filter === b.key, () => setFilter(b.key)))}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
        {/* 'record' = route-named admin entries (no medication name in the source) — Leo's Option 1 quarantine.
            Self-shrinking: the converter's name-recovery renames rows on future re-converts. */}
        {MED_BUCKETS.map((b) => section(b.key, b.section, b.dot, buckets[b.key]))}
      </Box>
      {noResults && (
        <Typography sx={{ p: '40px 26px', textAlign: 'center', color: '#8e8e93', fontSize: 15 }}>
          No records match &ldquo;{search}&rdquo;.
        </Typography>
      )}
      <Box sx={{ mt: 4.5, pt: 2.75, borderTop: '1px solid #e2e2e8', textAlign: 'center' }}>
        <Typography sx={{ fontSize: 12, color: '#aeaeb2', lineHeight: 1.55, maxWidth: 660, mx: 'auto' }}>
          This page organizes the medication records reported by your healthcare providers and pharmacies. It presents your
          health record as documented and does not provide medical or dosing advice.
        </Typography>
      </Box>
    </Container>
  );
};
